/**
 * Bounded `errorKind` token for a rejection that did NOT come from axios.
 *
 * `sanitizeError` projects an `AxiosError` to `{ status, code }` and — by its
 * own docstring — EVERYTHING ELSE to `{}`. On a Tauri IPC or event rejection it
 * therefore logs a literal empty object, so "the release never happened" and
 * "the listener never attached" arrive in Datadog with nothing to tell them
 * apart. This is the field that carries the diagnosis; `sanitizeError` still
 * carries the axios projection beside it, because a call site cannot always
 * know which kind of rejection it caught.
 *
 * What it may return is closed by construction: a Rust-side `HasErrorCode`
 * enum string (`SELF_HEALING_STALE_EPOCH`, `ONLINE_SESSION_HTTP`, …), an error
 * CLASS name (`SelfHealingBridgeError`, `TypeError`), or `unknown`. The error
 * MESSAGE is never read — that is where a backup file path or a session id
 * would be, and the Datadog transport redacts nothing.
 *
 * Duck-typed rather than `instanceof`, because a rejected `invoke()` is not
 * reliably an `Error`: Tauri rejects with whatever Rust serialised, which can
 * be a plain object or a bare string.
 */
export function bridgeErrorKind(error: unknown): string {
  if (typeof error !== 'object' || error === null) return 'unknown';
  const { errorCode, name } = error as { errorCode?: unknown; name?: unknown };
  if (typeof errorCode === 'string' && errorCode !== '') return errorCode;
  if (typeof name === 'string' && name !== '') return name;
  return 'unknown';
}
